import { Group, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { getPaginationRowModel } from "@tanstack/react-table"
import { ColumnManager, DEFAULT_PAGE_SIZE, Loader } from "@tfg/ui"
import { useState, useCallback, useMemo, Dispatch, SetStateAction } from "react"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindIngestionsFiltersDto,
  FindIngestionsResponseDto,
  useFindIngestions,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { ErrorPreviewModal } from "~/pages/Admin/Ingestion/ProductImport/components/ErrorPreviewModal"
import { useColumns } from "~/pages/Brand/DataTransfer/Import/columns"
import { IngestionListFilters } from "~/pages/Brand/DataTransfer/Import/components/IngestionListFilters"
import {
  IngestionSortsType,
  IngestionsSorts,
} from "~/pages/Brand/DataTransfer/Import/components/IngestionsSorts"

type Props = {
  sorts: IngestionSortsType
  filters: FindIngestionsFiltersDto
  setSorts: Dispatch<SetStateAction<IngestionSortsType>>
  setFilters: Dispatch<SetStateAction<FindIngestionsFiltersDto>>
}

const EMPTY_INGESTIONS: FindIngestionsResponseDto[] = []

export const IngestionsList = ({
  sorts,
  filters,
  setSorts,
  setFilters,
}: Props) => {
  const [selectedIngestionId, setSelectedIngestionId] = useState<string>("")

  const [errorModalOpened, errorModalActions] = useDisclosure(false)

  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const { data: ingestions, isPending: isLoadingIngestions } =
    useFindIngestions(
      { sorts, filters },
      {
        query: {
          refetchInterval: 10000, // 10 seconds
        },
      }
    )

  const columnManagerTranslations = useGetColumnManagerTranslations()

  const handleErrorModalOpen = useCallback(
    (ingestionId: string) => {
      setSelectedIngestionId(ingestionId)
      errorModalActions.open()
    },
    [errorModalActions.open]
  )

  const columns = useColumns({
    handleErrorModalOpen,
  })

  const paginationRowModel = useMemo(() => getPaginationRowModel(), [])
  const tableData = ingestions?.data ?? EMPTY_INGESTIONS

  const table = useCustomReactTable({
    data: tableData,
    columns,
    initialState: {
      pagination: {
        pageSize: DEFAULT_PAGE_SIZE,
      },
    },
    enableSorting: false,

    getPaginationRowModel: paginationRowModel,
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.DATA_TRANSFER_IMPORT} />
      <Group>
        <IngestionsSorts sorts={sorts} setSorts={setSorts} />
        <IngestionListFilters filters={filters} setFilters={setFilters} />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <ViewHeader tableContext={TABLE_CONTEXT_MAP.DATA_TRANSFER_IMPORT} />
      </Group>
      {isLoadingIngestions ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          setOpenColumnManager={setIsColumnManagerOpen}
        />
      )}
      {errorModalOpened ? (
        <ErrorPreviewModal
          ingestionId={selectedIngestionId}
          opened={errorModalOpened}
          onClose={errorModalActions.close}
        />
      ) : null}
    </Stack>
  )
}
